/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';

import { MicroLabel, SurfaceCard, Text } from '@/shared/ui';

import { SALE_STATE_ACCENTS, useSaleStateLabel, type SaleState } from '../constants/sale-states';
import { formatCalendarDate } from './sale-detail-atoms';
import type { Sale } from '../types/sale.types';

/**
 * Where the sale stands as a record, at the top of the right column.
 *
 * This was the fourth KPI card, sitting in a row of figures while saying
 * nothing numeric. Moving it out of that row leaves the strip to the four
 * amounts and gives the record its own quiet corner beside the activity it
 * summarises.
 *
 * There is no conclusion date to show. The `Sale` model carries no
 * `completed_at` or `voided_at`, so `paperwork.registry` is the one honest
 * date available and the hint says so rather than inventing one.
 */

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The most informative true detail for the record's current state.
 *
 * @param sale - The sale record.
 * @param t - The namespace translator.
 * @returns The hint line.
 */
export function recordHint(sale: Sale, t: Translate): string {
  if (sale.state === 'voided') {
    return sale.cancellation_reason || t('detail.kpiCards.registryVoidedNoDate');
  }
  if (sale.state === 'completed') {
    const date = formatCalendarDate(sale.paperwork.registry);
    return date
      ? t('detail.kpiCards.registryCompletedOn', { date })
      : t('detail.kpiCards.registryCompletedNoDate');
  }
  return t('detail.kpiCards.registryInProgress');
}

/**
 * The record card.
 *
 * @param props.sale - The sale record.
 * @returns The rendered card.
 */
export function SaleRecordCard({ sale }: { sale: Sale }) {
  const { t } = useTranslation('features/sales');
  const stateLabel = useSaleStateLabel();

  return (
    <SurfaceCard
      testId="sale-kpi-record"
      className="gap-(--space-2) px-(--space-5) py-(--space-4)"
    >
      <MicroLabel>{t('detail.kpiCards.registry')}</MicroLabel>
      <Text
        component="span"
        style={{ color: SALE_STATE_ACCENTS[sale.state as SaleState] }}
        className="text-[length:var(--text-app-section)] [font-weight:var(--font-semibold)] leading-tight"
      >
        {stateLabel(sale.state)}
      </Text>
      <Text component="span" variant="bodySm" tone="secondary" className="leading-relaxed">
        {recordHint(sale, t)}
      </Text>
    </SurfaceCard>
  );
}

export default SaleRecordCard;
